import React from "react";
import { motion } from "framer-motion";
import {
  Briefcase, ShoppingBag, Rocket, User, RefreshCcw, Wrench, ArrowUpRight,
} from "lucide-react";

const services = [
  {
    icon: Briefcase,
    title: "Business Websites",
    desc: "Trust-building corporate sites that turn visitors into qualified inquiries.",
    tag: "Most Popular",
  },
  {
    icon: ShoppingBag,
    title: "E-Commerce Stores",
    desc: "Fast, secure online stores with payment gateways, inventory and SEO.",
  },
  {
    icon: Rocket,
    title: "Landing Pages",
    desc: "High-converting single pages built around one specific offer or campaign.",
  },
  {
    icon: User,
    title: "Portfolio Websites",
    desc: "Elegant personal sites that get freelancers, creators and consultants hired.",
  },
  {
    icon: RefreshCcw,
    title: "Website Redesign",
    desc: "Modernize your outdated site with premium UX, speed and a stronger funnel.",
  },
  {
    icon: Wrench,
    title: "Website Maintenance",
    desc: "Ongoing updates, security patches, backups and performance optimisations.",
  },
];

export const Services = ({ onCTA }) => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 sm:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
          <div className="max-w-2xl">
            <div className="text-[11px] tracking-[0.2em] uppercase text-[#D4AF37] mb-4">
              What I Build
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light text-white">
              Websites built to <span className="gold-text italic">generate leads.</span>
            </h2>
          </div>
          <p className="max-w-md text-[#B0B0B0]">
            Every project is custom-coded, mobile-first and obsessively optimised for
            speed, search and conversions — not just looks.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              data-testid={`service-card-${i}`}
              className="group relative bg-[#0A0A0A] border border-[#222222] rounded-2xl p-7 transition-all duration-500 hover:border-[#D4AF37]/40 hover:-translate-y-1"
            >
              {s.tag && (
                <span className="absolute top-5 right-5 text-[10px] tracking-[0.18em] uppercase text-[#D4AF37] border border-[#D4AF37]/30 rounded-full px-2.5 py-1">
                  {s.tag}
                </span>
              )}
              <div className="w-12 h-12 rounded-xl glass-gold flex items-center justify-center mb-6">
                <s.icon className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <h3 className="font-display text-2xl text-white mb-3">{s.title}</h3>
              <p className="text-sm text-[#B0B0B0] leading-relaxed mb-6">{s.desc}</p>
              <button
                data-testid={`service-cta-${i}`}
                onClick={onCTA}
                className="inline-flex items-center gap-1.5 text-sm text-white hover:text-[#D4AF37] transition-colors"
              >
                Discuss this project
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
